import React from 'react';
import { Box, Typography } from '@mui/material';
import { isJsonString, getJsonDifferences, formatValueForDisplay } from 'utils/json-diff.utils';

interface JsonDiffCellProps {
  oldValue?: string;
  currentValue?: string;
  isOldValue?: boolean;
}

const renderJsonContent = (obj: any, depth = 0): React.ReactNode => {
  if (obj === null || obj === undefined) {
    return <span>null</span>;
  }

  if (typeof obj !== 'object' || Array.isArray(obj)) {
    return <span>{formatValueForDisplay(obj)}</span>;
  }

  const indent = depth * 16;

  return (
    <Box sx={{ marginLeft: `${indent}px` }}>
      {Object.entries(obj).map(([key, value], index) => (
        <Box key={index} sx={{ marginBottom: 0.25 }}>
          <Typography variant="body2" component="div">
            <strong>{key}:</strong>{' '}
            {typeof value === 'object' && value !== null && !Array.isArray(value) ? (
              <Box component="span">
                <Box sx={{ marginTop: 0.25 }}>{renderJsonContent(value, depth + 1)}</Box>
              </Box>
            ) : (
              formatValueForDisplay(value)
            )}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export const JsonDiffCell: React.FC<JsonDiffCellProps> = ({
  oldValue,
  currentValue,
  isOldValue = false,
}) => {
  const isOldJson = isJsonString(oldValue || '');
  const isCurrentJson = isJsonString(currentValue || '');

  if (!isOldJson && !isCurrentJson) {
    return <>{isOldValue ? oldValue : currentValue}</>;
  }

  if (isOldJson && !isCurrentJson) {
    if (isOldValue) {
      const parsedOld = JSON.parse(oldValue || '');
      return <Box>{renderJsonContent(parsedOld)}</Box>;
    } else {
      return <>{currentValue}</>;
    }
  }

  if (!isOldJson && isCurrentJson) {
    if (!isOldValue) {
      const parsedCurrent = JSON.parse(currentValue || '');
      return <Box>{renderJsonContent(parsedCurrent)}</Box>;
    } else {
      return <>{oldValue}</>;
    }
  }

  const differences = getJsonDifferences(oldValue || '', currentValue || '');

  if (differences.length === 0) {
    return <Typography variant="body2">No changes detected</Typography>;
  }

  return (
    <Box>
      {differences.map((diff, index) => (
        <Box key={index} sx={{ marginBottom: 0.5 }}>
          <Typography variant="body2" component="div">
            <strong>{diff.propertyName}:</strong>{' '}
            {formatValueForDisplay(isOldValue ? diff.oldValue : diff.currentValue)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};
